import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, applyRoleFilter, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { matchesFilters } from './helpers/filterHelpers';
import {
  getUserPermissions,
  getUserRole,
} from '../../../helper/permissionsHelper';
import camelcaseKeys from 'camelcase-keys';

// MakerHub: minutes a reply must age before its conversation counts as "Pending"
// (a fresh reply isn't an overdue follow-up yet). Mirrors Conversation::MH_PENDING_GRACE_MINUTES.
const MH_PENDING_GRACE_MINUTES = 30;
// A genuine holding reply is SHORT and defers; a long last reply is an answer that
// already resolved the question. Cap length to separate them. Mirrors the server scope.
const MH_PENDING_MAX_LEN = 200;
// MakerHub: client mirror of Conversation::MH_PENDING_HOLDING_RE — a holding/promise
// reply we owe a follow-up on (English + Malay/rojak). The server scope is authoritative
// for the Pending badge count; this getter only decides which rows show in the list.
const PENDING_HOLDING_RE = new RegExp(
  [
    'let me (check|confirm|see|find out|ask|update|revert|get ?back)',
    "i'?ll (check|confirm|update|revert|get ?back|find out|ask|send|let you know|see)",
    'i will (check|confirm|update|revert|get ?back|find out|ask|send)',
    "we'?ll (check|confirm|update|revert|get ?back|send|arrange)",
    'will (check|confirm|update|revert|send|arrange|get ?back|let you know)',
    '(get|revert|come|getting) back to you',
    'update (you|u)( later| soon| once| after)?',
    'checking (for|on|with|now)',
    'hold on',
    'one moment',
    'give me a (sec|second|min|minute|moment)',
    'nanti (saya |i )?(check|semak|cek|update|bagitau|confirm|reply|balas|tanya)',
    'akan (saya )?(check|semak|update|confirm|bagitau|reply)',
    '\\bjap\\b',
    'sekejap',
    'kejap',
    '(semak|check|confirm|tanya|cek) dulu',
    'tunggu (sebentar|sekejap|jap)',
    '(saya|i) (semak|check|cek|tanya|update|confirm)',
    'bagi (i|saya) (check|semak|cek|tanya|masa)',
  ].join('|'),
  'i'
);
// MakerHub: exclude our own templated Night Concierge off-hours auto-reply from
// Pending (a system deferral handled by the night->morning briefing, not a
// forgotten promise). Mirrors the server scope's exclusion.
const PENDING_EXCLUDE_RE =
  /luar waktu kerja|outside working hours|ai assistant makerhub|night concierge/i;

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

// MakerHub: WhatsApp groups are segregated into the Group tab. They carry the
// @g.us contact identifier Evolution assigns to group JIDs (mirrors the
// server-side mh_group/mh_not_group scopes).
const isGroupConversation = conversation =>
  (conversation.meta?.sender?.identifier || '').endsWith('@g.us');

export const needsReplyComparator = (a, b) => {
  // Literal last-visible-sender ordering — mirrors the server's
  // LastVisibleSenderQuery cursor order (unreplied first, then last visible
  // message recency). The strict radar fields stay on the rows for the
  // Urgent tab mirror; ordering must not use them.
  const unrepliedDelta =
    Number(Boolean(b.mh_unreplied)) - Number(Boolean(a.mh_unreplied));
  if (unrepliedDelta) return unrepliedDelta;

  const recencyDelta =
    Number(b.mh_last_visible_at || b.last_activity_at || 0) -
    Number(a.mh_last_visible_at || a.last_activity_at || 0);
  if (recencyDelta) return recencyDelta;

  const messageIdDelta =
    Number(b.mh_last_visible_id || 0) - Number(a.mh_last_visible_id || 0);
  return messageIdDelta || Number(b.id) - Number(a.id);
};

const getters = {
  getAllConversations: ({ allConversations, chatSortFilter: sortKey }) => {
    return allConversations.sort((a, b) => sortComparator(a, b, sortKey));
  },
  getFilteredConversations: (
    { allConversations, appliedFilters },
    _,
    __,
    rootGetters
  ) => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    return allConversations
      .filter(conversation => {
        const matchesFilterResult = matchesFilters(
          conversation,
          appliedFilters
        );
        const allowedForRole = applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId
        );

        return matchesFilterResult && allowedForRole;
      })
      .sort(needsReplyComparator);
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: ({ selectedChatId, attachments }) => {
    return attachments[selectedChatId] || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const { message_type: messageType } = message;
      if (message.private) return false;

      return [MESSAGE_TYPE.OUTGOING, MESSAGE_TYPE.INCOMING].includes(
        messageType
      );
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;

    return _state.allConversations.filter(conversation => {
      if (isGroupConversation(conversation)) return false;
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserID;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isChatMine = isAssignedToMe && shouldFilter;

      return isChatMine;
    });
  },
  getAppliedConversationFiltersV2: _state => {
    // TODO: Replace existing one with V2 after migrating the filters to use camelcase
    return _state.appliedFilters.map(camelcaseKeys);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: _state => activeFilters => {
    return _state.allConversations.filter(conversation => {
      if (isGroupConversation(conversation)) return false;
      const isUnAssigned = !conversation.meta.assignee;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return isUnAssigned && shouldFilter;
    });
  },
  // MakerHub: Group tab — only WhatsApp group conversations (@g.us), the inverse
  // of the @g.us exclusion applied to All/Mine/Urgent. Mirrors Conversation.mh_group.
  getGroupChats: _state => activeFilters => {
    return _state.allConversations.filter(conversation => {
      if (!isGroupConversation(conversation)) return false;
      return applyPageFilters(conversation, activeFilters);
    });
  },
  // MakerHub: Urgent tab — action-required queue. Mirrors Conversation.mh_urgent
  // server scope: the customer's message is the last real (non-private) word —
  // excludes sync shells with no real message, replied-last, WhatsApp groups
  // (@g.us) and __* test inboxes. Replying clears waiting_since; "Mark handled"
  // stamps custom_attributes.mh_handled_at; a newer customer message bumps
  // last_activity_at past the stamp and re-urgents it.
  getUrgentChats: (_state, _, __, rootGetters) => activeFilters => {
    const nowSec = Math.floor(Date.now() / 1000);
    return _state.allConversations.filter(conversation => {
      // Open-only queue (mirrors the finder): the list defaults to ALL statuses,
      // but a thread automation already resolved is not "waiting on us".
      if (conversation.status !== 'open') return false;
      // Explicitly flagged urgent (staff/AI via toggle_priority) always surfaces
      // here, on top of the computed "customer is waiting on us" queue below.
      if (
        conversation.priority === 'urgent' &&
        !isGroupConversation(conversation)
      ) {
        return applyPageFilters(conversation, activeFilters);
      }
      if (isGroupConversation(conversation)) return false;
      const inbox = rootGetters['inboxes/getInbox'](conversation.inbox_id);
      if (inbox?.name?.startsWith('__')) return false;
      // Branch 1 is server-owned. Never re-derive message classification in Vue:
      // the row flag is the same tuple predicate used by ordering and the badge.
      const customerWaiting = conversation.mh_needs_reply === true;
      const handledAt = conversation.custom_attributes?.mh_handled_at || 0;
      const extraBranchesAllowed =
        handledAt < (conversation.last_activity_at || 0);
      // Branch 2: a promise we made (mh_cs.owed) has gone overdue (mh_cs.due elapsed)
      const mhCs = conversation.custom_attributes?.mh_cs || {};
      const owed = mhCs.owed === true || mhCs.owed === 'true';
      const due = Number(mhCs.due) || 0;
      const overdue = extraBranchesAllowed && owed && due > 0 && due <= nowSec;
      // Branch 4 (Phase B, mirrors Conversation.mh_urgent branch 4): the AI handed off — a
      // held/deferred draft (or escalation) flagged need-human. ai_state (server-computed
      // from the latest private note's content_attributes, already on the list payload) is
      // the single signal; it self-heals (a human reply makes the latest non-activity message
      // non-private → ai_state nil). Surfaces held 💡 drafts that branch 1 misses when a
      // "we'll get back to you" non-private ack landed after the customer.
      const needsHumanHandoff =
        extraBranchesAllowed && conversation.ai_state?.needs_human === true;
      if (!customerWaiting && !overdue && !needsHumanHandoff) return false;
      return applyPageFilters(conversation, activeFilters);
    });
  },
  // MakerHub: Pending tab — conversations where WE owe a follow-up. Mirrors
  // Conversation.mh_pending: WE have the last real (non-private) word (a holding
  // reply), aged past the grace, not handled, not a group/__test inbox, and either
  // AI-flagged (custom_attributes.mh_cs.owed) or the last outgoing reads like a promise.
  getPendingChats: (_state, _, __, rootGetters) => activeFilters => {
    const graceCutoff =
      Math.floor(Date.now() / 1000) - MH_PENDING_GRACE_MINUTES * 60;
    return _state.allConversations.filter(conversation => {
      // Open-only queue (mirrors the finder) — see getUrgentChats.
      if (conversation.status !== 'open') return false;
      // Explicitly pinned to Pending (staff/AI via toggle_pending) always surfaces
      // here, alongside the computed "we owe a follow-up" queue below.
      const pinPending = conversation.custom_attributes?.pin_pending;
      if (
        (pinPending === true || pinPending === 'true') &&
        !isGroupConversation(conversation)
      ) {
        return applyPageFilters(conversation, activeFilters);
      }
      const handledAt = conversation.custom_attributes?.mh_handled_at || 0;
      const lastActivityAt = conversation.last_activity_at || 0;
      if (handledAt >= lastActivityAt) return false;
      if (lastActivityAt >= graceCutoff) return false;
      if (isGroupConversation(conversation)) return false;
      const inbox = rootGetters['inboxes/getInbox'](conversation.inbox_id);
      if (inbox?.name?.startsWith('__')) return false;
      const chatMessages = (conversation.messages || []).filter(
        msg =>
          !msg.private &&
          (msg.message_type === 0 || msg.message_type === 1) &&
          msg.content_attributes?.mh_keep_unreplied?.toString() !== 'true'
      );
      const lastReal = chatMessages.length
        ? chatMessages[chatMessages.length - 1]
        : conversation.last_non_activity_message;
      if (
        lastReal?.content_attributes?.mh_keep_unreplied?.toString() === 'true'
      ) {
        return false;
      }
      // WE must have the last real word (outgoing) — the opposite of Urgent
      if (!lastReal || lastReal.message_type !== 1) return false;
      // a long last reply is an answer that resolved the question, not a deferral
      if ((lastReal.content || '').length >= MH_PENDING_MAX_LEN) return false;
      const owed = conversation.custom_attributes?.mh_cs?.owed;
      const isOwed = owed === true || owed === 'true';
      if (!isOwed && !PENDING_HOLDING_RE.test(lastReal.content || '')) {
        return false;
      }
      // Exclude our own templated Night Concierge off-hours auto-reply — it's a
      // system deferral handled by the night->morning briefing, not a forgotten
      // promise. Mirrors the server scope's exclusion.
      if (PENDING_EXCLUDE_RE.test(lastReal.content || '')) return false;
      // Once a promise goes overdue (mh_cs.due elapsed) it escalates to Urgent —
      // drop it from Pending so it's in at most one red tab (mirrors mh_pending).
      const due = Number(conversation.custom_attributes?.mh_cs?.due) || 0;
      if (due > 0 && due <= Math.floor(Date.now() / 1000)) return false;
      return applyPageFilters(conversation, activeFilters);
    });
  },
  getAllStatusChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    return _state.allConversations
      .filter(conversation => {
        if (isGroupConversation(conversation)) return false;
        const shouldFilter = applyPageFilters(conversation, activeFilters);
        const allowedForRole = applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId
        );

        return shouldFilter && allowedForRole;
      })
      .sort(needsReplyComparator);
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },

  getCopilotAssistant: _state => {
    return _state.copilotAssistant;
  },
};

export default getters;
